import { useForm } from 'react-final-form';
import styles from './FinancialFileItem.module.scss';
import { RiCloseFill as RemoveIcon, RiErrorWarningFill } from 'react-icons/ri'
import PdfIcon from '../../../assets/images/financeStatement/revenueDetails/pdf.svg';
import ExcelIcon from '../../../assets/images/financeStatement/revenueDetails/excel.svg';
import { useCallback, useContext, useState } from 'react';
import { UnderwritingFormContext } from '../context/UnderwritingFormContext';
import { ApplicationContextType, MonthlyReportSteps, UnderwritingSteps, uploadStates } from '../../../interfaces/interfaces';
import CircularLoader from '../../../components/common/CircularLoader/CircularLoader';
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import PdfPasswordProtection from '../components/MultiFileUploadModal/PdfPasswordProtection';
import { getIn } from 'final-form';
import { MonthlyReportFormContext } from '../../MonthlyReport/context/MonthlyReportFormContext';
import { HiCheck } from 'react-icons/hi';


type ClassificationEntry = {
    classification: 'BS' | 'PL' | 'BOTH' | '';
    docId: number;
    error: any;
}

type Props = {
    fieldName: string;
    fieldIndex: number;
    stepType: UnderwritingSteps | MonthlyReportSteps;
    outerFieldName: string;
    newUploadedDocIds: number[];
    setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
    deleteDocIds: number[],
    setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
    isJobProcessing?: boolean;
    contextType?: ApplicationContextType;
    fyClassificationResult?: ClassificationEntry[] | null;
    fyClassificationErrorDetails?: any;
}


const FinancialFileItem = ({ fieldName, stepType, outerFieldName, setDeleteDocIds, setNewUploadedDocIds, deleteDocIds, newUploadedDocIds, fieldIndex, isJobProcessing, contextType = ApplicationContextType.underwriting, fyClassificationResult, fyClassificationErrorDetails }: Props) => {

    const { handleUnderwritingFileJobs, underwritingFileJobs, sendFileJobToApi } = useContext(contextType == ApplicationContextType?.monthlyReport ? MonthlyReportFormContext : UnderwritingFormContext);
    const [showPasswordSuccess, setShowPasswordSuccess] = useState(false);
    const { getState, mutators } = useForm()

    const getJobDetails = useCallback((fieldName) => {
        const jobDetails = underwritingFileJobs?.[stepType]?.[fieldName] ?? null;
        return jobDetails;
    }, [underwritingFileJobs]);

    const handlePasswordSubmit = (password: string, fieldName: string) => {
        setShowPasswordSuccess(true);
        const fileJobDetails = getJobDetails(fieldName);
        sendFileJobToApi(stepType, fieldName, {
            ...fileJobDetails,
            fileMetaData: {
                ...fileJobDetails.fileMetaData,
                password: password,
            },
            status: uploadStates.doneUploading,
        });
    }

    const fileUploadedValue = getIn(getState()?.values, `${fieldName}`)
    const docId = fileUploadedValue?.uploadedFile?.docId ?? null;

    const docClassification: ClassificationEntry | null = fyClassificationResult?.find((c) => c.docId === docId) ?? null;

    const hasFyErrorState = fyClassificationErrorDetails?.error_state && Object.keys(fyClassificationErrorDetails.error_state).length > 0;
    const hasDocError = hasFyErrorState && docClassification?.error && Object.keys(docClassification.error).length > 0;

    const getClassificationTags = (): string[] => {
        if (!docClassification?.classification) return [];
        if (docClassification.classification === 'BS') return ['Balance Sheet'];
        if (docClassification.classification === 'PL') return ['P&L'];
        if (docClassification.classification === 'BOTH') return ['Balance Sheet', 'P&L'];
        return [];
    }

    const getFileType = () => {
        if (fileUploadedValue?.selectedFile instanceof File) {
            return fileUploadedValue.selectedFile.type;
        }
        return fileUploadedValue?.uploadedFile?.fileType ?? '';
    }

    const isExcel = () => {
        const fileType = getFileType();
        return fileType === 'application/vnd.ms-excel' || fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }

    const classificationTags = getClassificationTags();
    const hideClassificationUi = Boolean(isJobProcessing);

    const clipFileName = (name: string): string => {
        if (!name || name.length <= 36) return name;
        return name.slice(0, 30) + '...' + name.slice(-3);
    };

    return (
        <>
            {contextType == ApplicationContextType.monthlyReport && fileUploadedValue?.uploadedFile?.isOldFile ?
                null
                :
                <div className={styles.fileItemRootContainer}>

                    <div className={styles.fileItemContainer}>
                        <div className={styles.leftContainer}>
                            <div className={styles.iconContainer}>
                                <div className={styles.fileIconWrapper}>
                                    {isExcel() ? <img src={ExcelIcon} alt="excel" /> : <img src={PdfIcon} alt="pdf" />}
                                    {hasDocError && !hideClassificationUi && (
                                        <RiErrorWarningFill className={styles.fileIconWarningBadge} />
                                    )}
                                </div>
                            </div>
                            <div className={styles.fileNameContainer}>
                                <div className={styles.fileName}>
                                    {clipFileName(fileUploadedValue.selectedFile instanceof File ? fileUploadedValue?.selectedFile?.name : fileUploadedValue?.uploadedFile?.fileName)}
                                </div>
                                {classificationTags.length > 0 && !hideClassificationUi && (
                                    <div className={styles.classificationTags}>
                                        {classificationTags.map((tag) => (
                                            <div key={tag} className={styles.classificationTag}>
                                                <HiCheck className={styles.classificationIcon} />
                                                <div className={styles.classificationText}>{tag}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                        </div>
                        <div className={styles.rightContainer}>
                            {getJobDetails(fieldName)?.status == uploadStates.uploading || isJobProcessing ?
                                <CircularLoader />
                                : <MeritoButton
                                    type='tertiary'
                                    onlyIcon={true}
                                    icon={<RemoveIcon />}
                                    onClick={() => {
                                        console.log('outerFieldName', outerFieldName, fieldIndex);
                                        console.log('fileUploadedValue', fileUploadedValue);
                                        setDeleteDocIds(prev => [...prev, fileUploadedValue.uploadedFile?.docId]);
                                        mutators.remove(outerFieldName, fieldIndex);
                                    }}
                                />}
                        </div>
                    </div>

                    {getJobDetails(fieldName)?.status == uploadStates.requiredPassword || showPasswordSuccess ?
                        <PdfPasswordProtection showPasswordSuccess={showPasswordSuccess} fileDetails={fileUploadedValue} handleSubmitPassword={(password) => handlePasswordSubmit(password, fieldName)} />
                        : null}
                </div>}
        </>
    );
}

export default FinancialFileItem;
